import dayjs from 'dayjs';
import type {
  CustomerListItemSummary,
  CustomerSegmentRow,
  DashboardRow,
  ReservationListRow,
} from '../api-client.ts';

/**
 * Escape a CSV field per RFC 4180: wrap in double quotes if it contains comma,
 * newline, or double quote; double up internal quotes.
 */
function csvField(v: string | number): string {
  const s = String(v);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function csvRow(values: (string | number)[]): string {
  return values.map(csvField).join(',');
}

const STATUS_ZH: Record<string, string> = {
  pending: '待確認',
  confirmed: '已確認',
  checked_in: '客人到場',
  completed: '已完成',
  canceled: '已取消',
};

export function rowsToCsv(rows: readonly DashboardRow[], timezone: string): string {
  const header = csvRow([
    '預約編號',
    '開始時間',
    '結束時間',
    '人數',
    '狀態',
  ]);
  const body = rows.map((r) =>
    csvRow([
      r.reservationId,
      dayjs(r.startTime).tz(timezone).format('YYYY-MM-DD HH:mm'),
      dayjs(r.endTime).tz(timezone).format('YYYY-MM-DD HH:mm'),
      r.partySize,
      STATUS_ZH[r.status] ?? r.status,
    ]),
  );
  // BOM so Excel recognises UTF-8 for Chinese characters.
  return '\ufeff' + [header, ...body].join('\r\n') + '\r\n';
}

const STATUS_ZH_FULL: Record<string, string> = {
  pending: '待確認',
  confirmed: '已確認',
  checked_in: '客人到場',
  completed: '已完成',
  canceled: '已取消',
  no_show: '未到',
};

const PAYMENT_ZH: Record<string, string> = {
  unpaid: '未付款',
  partial: '部分已付',
  paid: '已付清',
  refunded: '已退款',
};

export function reservationListToCsv(
  rows: readonly ReservationListRow[],
  timezone: string,
): string {
  const header = csvRow([
    '預約編號',
    '開始時間',
    '結束時間',
    '人數',
    '狀態',
    '客戶名稱',
    '客戶編號',
    '客戶備註',
    '訂位備註',
    '來源',
    '應收金額',
    '已收金額',
    '幣別',
    '付款狀態',
    '付款時間',
  ]);
  const body = rows.map((r) =>
    csvRow([
      r.reservationId,
      dayjs(r.startTime).tz(timezone).format('YYYY-MM-DD HH:mm'),
      dayjs(r.endTime).tz(timezone).format('YYYY-MM-DD HH:mm'),
      r.partySize,
      STATUS_ZH_FULL[r.status] ?? r.status,
      r.customerName ?? '',
      r.customerId ?? '',
      r.customerRef ?? '',
      r.notes ?? '',
      r.source,
      r.totalAmount ?? '',
      r.paidAmount ?? '',
      r.currency ?? '',
      PAYMENT_ZH[r.paymentStatus] ?? r.paymentStatus,
      r.paidAt ? dayjs(r.paidAt).tz(timezone).format('YYYY-MM-DD HH:mm') : '',
    ]),
  );
  return '\ufeff' + [header, ...body].join('\r\n') + '\r\n';
}

export function customersToCsv(items: readonly CustomerListItemSummary[], timezone: string): string {
  const header = csvRow([
    '客戶編號',
    '顯示名稱',
    '聯絡方式',
    '標籤',
    '備註',
    '訪問次數',
    '最近訪問',
    '建立時間',
    '封存',
  ]);
  const body = items.map((i) => {
    const c = i.customer;
    const contactStr = c.contactMethods.map((m) => `${m.kind}:${m.value}`).join('; ');
    const tagStr = c.tags.join('; ');
    const lastVisit = i.lastVisitIso
      ? dayjs(i.lastVisitIso).tz(timezone).format('YYYY-MM-DD HH:mm')
      : '';
    return csvRow([
      c.customerId,
      c.displayName,
      contactStr,
      tagStr,
      c.note ?? '',
      i.reservationCount,
      lastVisit,
      dayjs(c.createdAt).tz(timezone).format('YYYY-MM-DD HH:mm'),
      c.archivedAt ? '是' : '',
    ]);
  });
  return '\ufeff' + [header, ...body].join('\r\n') + '\r\n';
}

const TIER_ZH: Record<string, string> = {
  normal: '一般',
  vip: 'VIP',
  blacklist: '注意',
};

export function segmentToCsv(
  rows: readonly CustomerSegmentRow[],
  timezone: string,
): string {
  const header = csvRow([
    '客戶編號',
    '姓名',
    '分級',
    '標籤',
    '訪問次數',
    '最近造訪',
    '生日',
    '累計消費 (TWD)',
  ]);
  const body = rows.map((r) => {
    const bday = r.birthday ? `${r.birthday.month}/${r.birthday.day}` : '';
    const lastVisit = r.lastVisitIso
      ? dayjs(r.lastVisitIso).tz(timezone).format('YYYY-MM-DD')
      : '';
    return csvRow([
      r.customerId,
      r.displayName,
      TIER_ZH[r.tier] ?? r.tier,
      r.tags.join('; '),
      r.visitCount,
      lastVisit,
      bday,
      r.totalSpentTwd,
    ]);
  });
  return '\ufeff' + [header, ...body].join('\r\n') + '\r\n';
}

export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
